import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import {
  getCustomCategories,
  addCustomCategory,
  updateCustomCategory,
  deleteCustomCategory,
  type CustomCategory,
  type CustomCategoryInput,
} from '@/lib/firestore-categories';
import { GAIN_CATEGORIES, EXPENSE_CATEGORIES } from '@/types';

export interface MergedCategory {
  id: string;
  label: string;
  type: 'gain' | 'expense';
  color: string;
  isCustom: boolean;
}

interface CategoriesContextType {
  /** All gain categories (built-in + custom), ready for dropdowns */
  gainCategories: MergedCategory[];
  /** All expense categories (built-in + custom), ready for dropdowns */
  expenseCategories: MergedCategory[];
  /** Get label for any category id */
  getCategoryLabel: (id: string) => string;
  /** Get color for any category id */
  getCategoryColor: (id: string) => string;
  /** Raw custom categories (for the manager UI) */
  customCategories: CustomCategory[];
  addCategory: (cat: CustomCategoryInput) => Promise<void>;
  updateCategory: (id: string, updates: Partial<CustomCategoryInput>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  loading: boolean;
}

// Default colors for built-in categories
const BUILTIN_COLORS: Record<string, string> = {
  gain_1: '#10b981', gain_2: '#059669', gain_3: '#f59e0b', gain_4: '#3b82f6',
  gain_5: '#8b5cf6', gain_6: '#06b6d4', gain_7: '#ec4899', gain_8: '#6b7280',
  exp_1: '#ef4444', exp_2: '#f97316', exp_3: '#eab308', exp_4: '#84cc16',
  exp_5: '#14b8a6', exp_6: '#6366f1', exp_7: '#8b5cf6', exp_8: '#ec4899',
  exp_9: '#f43f5e', exp_10: '#6b7280',
};

const CategoriesContext = createContext<CategoriesContextType>({
  gainCategories: [],
  expenseCategories: [],
  getCategoryLabel: (id) => id,
  getCategoryColor: () => '#6b7280',
  customCategories: [],
  addCategory: async () => {},
  updateCategory: async () => {},
  deleteCategory: async () => {},
  loading: true,
});

export function CategoriesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const language = settings?.language || 'en';

  const [customCategories, setCustomCategories] = useState<CustomCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = useCallback(async () => {
    if (!user) {
      setCustomCategories([]);
      setLoading(false);
      return;
    }
    try {
      const cats = await getCustomCategories(user.uid);
      setCustomCategories(cats);
    } catch (e) {
      console.error('Error loading custom categories:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Merge built-in + custom into MergedCategory arrays
  const gainCategories: MergedCategory[] = [
    ...Object.entries(GAIN_CATEGORIES).map(([id, labels]) => ({
      id,
      label: labels[language as 'en' | 'fr'] || labels.en,
      type: 'gain' as const,
      color: BUILTIN_COLORS[id] || '#10b981',
      isCustom: false,
    })),
    ...customCategories
      .filter((c) => c.type === 'gain')
      .map((c) => ({ id: c.id, label: c.name, type: 'gain' as const, color: c.color, isCustom: true })),
  ];

  const expenseCategories: MergedCategory[] = [
    ...Object.entries(EXPENSE_CATEGORIES).map(([id, labels]) => ({
      id,
      label: labels[language as 'en' | 'fr'] || labels.en,
      type: 'expense' as const,
      color: BUILTIN_COLORS[id] || '#ef4444',
      isCustom: false,
    })),
    ...customCategories
      .filter((c) => c.type === 'expense')
      .map((c) => ({ id: c.id, label: c.name, type: 'expense' as const, color: c.color, isCustom: true })),
  ];

  const allCategories = [...gainCategories, ...expenseCategories];

  const getCategoryLabel = (id: string): string => {
    const found = allCategories.find((c) => c.id === id);
    return found ? found.label : id;
  };

  const getCategoryColor = (id: string): string => {
    const found = allCategories.find((c) => c.id === id);
    return found ? found.color : '#6b7280';
  };

  const addCategory = async (cat: CustomCategoryInput) => {
    if (!user) return;
    const newId = await addCustomCategory(user.uid, cat);
    setCustomCategories((prev) => [
      ...prev,
      { ...cat, id: newId, createdAt: new Date() },
    ]);
  };

  const updateCategory = async (id: string, updates: Partial<CustomCategoryInput>) => {
    if (!user) return;
    await updateCustomCategory(user.uid, id, updates);
    setCustomCategories((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
  };

  const deleteCategory = async (id: string) => {
    if (!user) return;
    await deleteCustomCategory(user.uid, id);
    setCustomCategories((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <CategoriesContext.Provider
      value={{
        gainCategories,
        expenseCategories,
        getCategoryLabel,
        getCategoryColor,
        customCategories,
        addCategory,
        updateCategory,
        deleteCategory,
        loading,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories() {
  return useContext(CategoriesContext);
}
